import { isShopify, useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import type { Platform } from '@sparklayer-io/spark-dash-ui/types/platform';
import { Accordion, Button, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { OnboardingChecklist, OnboardingChecklistItem } from './onboarding-checklist';
import { onboardingStepBadge } from './onboarding-step-badge';
import { OnboardingStepIcon } from './onboarding-step-icon';

const COMPONENT = 'features:home.components.onboarding.frontend' satisfies I18nPrefix;

function platformWidgetsDocsUrl(platform: Platform | undefined): string | null {
  if (isShopify(platform)) return DOCS_URLS.installingShopify;
  if (platform === 'bigcommerce') return DOCS_URLS.installingBigcommerce;
  if (platform === 'wix') return DOCS_URLS.installingWix;
  return null;
}

export function FrontendStep({
  isExpanded,
  onExpandedChange,
  loading,
  onToggle,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  loading: boolean;
  onToggle: () => void;
}) {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const complete = env.onboarding_frontend;
  const widgetsDocsUrl = platformWidgetsDocsUrl(env.connected_platform);

  const [installed, setInstalled] = useState<boolean>(false);
  const [reviewedAdvanced, setReviewedAdvanced] = useState<boolean>(false);
  const allChecked = installed && reviewedAdvanced;

  // Unchecking the step resets its checklist so the user re-acknowledges each item.
  useEffect(() => {
    if (!complete) {
      setInstalled(false);
      setReviewedAdvanced(false);
    }
  }, [complete]);

  return (
    <Accordion.Item
      title={t(`${COMPONENT}.title`)}
      subtitle={t(complete ? `${COMPONENT}.subtitle_complete` : `${COMPONENT}.subtitle_incomplete`)}
      icon={<OnboardingStepIcon complete={complete} />}
      badge={onboardingStepBadge(complete)}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <Text variant="secondary">
        {t(`${COMPONENT}.description`)} <LearnMoreLink link={DOCS_URLS.frontendIntegration} />
      </Text>

      <OnboardingChecklist>
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.installed.label`)}
          description={t(`${COMPONENT}.checklist.installed.description`)}
          learnMoreLink={widgetsDocsUrl ?? undefined}
          checked={installed}
          onChange={() => setInstalled((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.advanced.label`)}
          description={t(`${COMPONENT}.checklist.advanced.description`)}
          action={{
            onClick: () => navigationHandler.navigate('/storefront/widgets/tips'),
            label: t(`${COMPONENT}.checklist.advanced.configurations_link`),
          }}
          learnMoreLink={DOCS_URLS.frontendConfigurations}
          checked={reviewedAdvanced}
          onChange={() => setReviewedAdvanced((prev) => !prev)}
        />
      </OnboardingChecklist>

      <Text variant="secondary" size="sm">
        {t(`${COMPONENT}.checklist_hint`)} <LearnMoreLink link={DOCS_URLS.frontendChecklist} />
      </Text>

      <div>
        <Button
          variant={complete ? 'secondary' : 'primary'}
          disabled={!complete && !allChecked}
          loading={loading}
          onClick={onToggle}
        >
          {t(complete ? `${COMPONENT}.action_undo` : `${COMPONENT}.action`)}
        </Button>
      </div>
    </Accordion.Item>
  );
}
